import { Box, Stack, Typography, Divider, Chip } from '@mui/material';
import Iconify from '@/components/Iconify';
import ShowMoreCollapse from '@/components/ShowMoreCollapse';
import useMobile from '@/hooks/useMobile';

const GREEN_COLOR = '#139E65';
const RED_COLOR = '#C2185B';
const DEFAULT_COLOR = '#161A1C';
const VARIANTS = {
  default: { icon: 'ph:ticket-bold', color: DEFAULT_COLOR },
  promotion: { icon: 'ph:tag-bold', color: GREEN_COLOR },
  fan2fan: { icon: 'ph:arrows-clockwise-bold', color: RED_COLOR },
  soldOut: {
    icon: 'ph:ticket-bold',
    color: (theme) => theme.palette.text.disabled,
  },
};

function TicketCard({
  variant = 'default',
  color,
  isSelected,
  quantity,
  children,
  styles,
  onClick,
  standAlone = false
}) {
  return (
    <Box
      sx={{
        background: 'transparent',
        padding: 2,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        ...styles,
      }}
      onClick={onClick}
    >
      <Stack direction="row" alignItems="center" gap={standAlone ? 1 : 2}>
        {isSelected ? (
          <Chip
            label={quantity}
            size="small"
            sx={{
              'height': 20,
              'minWidth': 20,
              'backgroundColor': 'primary.light',
              'color': 'primary.contrastText',
              '& .MuiChip-label': { px: 0.75, fontSize: '12px' },
            }}
          />
        ) : (
          <Iconify
            icon={VARIANTS[variant].icon}
            fontSize={18}
            sx={{
              color: variant === 'default' ? color : VARIANTS[variant].color,
            }}
          />
        )}

        {children}
      </Stack>
    </Box>
  );
}

function TicketCardBody({ withDescription, description, disabled, children }) {

  const { isMobile } = useMobile();

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: '100%' }}
      >
        {children}
      </Stack>
      {withDescription && (
        <>
          <Divider sx={{ my: 1 }} />
          <ShowMoreCollapse disabled={disabled}>
            <Box
              sx={{
                'fontSize': isMobile ? '12px' : '14px',
                '& ul': {
                  ml: 3,
                },
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: description }}></div>
            </Box>
          </ShowMoreCollapse>
        </>
      )}
    </Box>
  );
}

function TicketCardInfo({
  title,
  withPromotion,
  children,
  variant = 'default',
  styles,
}) {

  const { isMobile } = useMobile();

  return (
    <Box sx={{ width: withPromotion ? '50%' : '67%', ...styles }}>
      <Stack gap={0.5}>
        <Typography
          sx={{
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 500,
            lineHeight: '130%',
            color: VARIANTS[variant].color,
          }}
        >
          {title}{' '}
        </Typography>
        {children}
      </Stack>
    </Box>
  );
}

function TicketCardAction({ withPromotion, styles, children }) {
  return (
    <Stack
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      gap={1}
      sx={{ width: withPromotion ? '50%' : '33%', ...styles }}
    >
      {children}
    </Stack>
  );
}

function TicketCardPrice({
  value,
  hasAdditionalFee,
  withMap,
  variant = 'default',
  isFree,
  isSoldOut,
  withPromotion,
  discountType,
  priceWithDiscount,
  bundleRule,
}) {

  const { isMobile } = useMobile();

  if (isFree)
    return (
      <Typography
        sx={{
          color: GREEN_COLOR,
          fontSize: isMobile ? '12px' : '14px',
          lineHeight: '130%',
        }}
      >
        Gratis
      </Typography>
    );

  return (
    <Stack direction={withMap ? 'column' : 'row'} sx={{ gap: withMap ? 0 : 0.5 }}>
      {variant === 'fan2fan' && (
        <Typography
          sx={{
            color: 'text.disabled',
            fontSize: isMobile ? '12px' : '14px',
            lineHeight: '130%',
            textAlign: 'left',
          }}
        >
          Fan a Fan desde{' '}
        </Typography>
      )}
      <Stack direction="row" gap={0.5}>
        {!isSoldOut && withPromotion && discountType !== 'bundle' && (
          <Typography
            sx={{
              color: GREEN_COLOR,
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: 500,
              lineHeight: '130%',
              whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            {priceWithDiscount}
          </Typography>
        )}
        {!isSoldOut && withPromotion && discountType === 'bundle' && (
          <Typography
            sx={{
              color: GREEN_COLOR,
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: 500,
              lineHeight: '130%',
              textAlign: 'right',
            }}
          >
            {bundleRule}
          </Typography>
        )}

        <Typography
          sx={{
            width: '100%',
            color:
              withPromotion && discountType !== 'bundle'
                ? 'text.disabled'
                : VARIANTS[variant].color,
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: 500,
            lineHeight: '130%',
            textAlign: 'right',
            whiteSpace: 'nowrap',
            textDecoration:
              !isSoldOut && withPromotion && discountType !== 'bundle'
                ? 'line-through'
                : 'none',
          }}
        >
          {value}
        </Typography>
      </Stack>

      {!hasAdditionalFee && variant !== 'fan2fan' && (
        <Typography
          sx={{
            color: (theme) => theme.palette.text.disabled,
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: 400,
            lineHeight: '130%',
            textAlign: 'right',
          }}
        >
          {' '}
          + cargos
        </Typography>
      )}
    </Stack>
  );
}

function TicketCardSoldOut() {

  const { isMobile } = useMobile();

  return (
    <Chip
      label="Agotado"
      variant="outlined"
      sx={{
        'color': (theme) => theme.palette.text.disabled,
        'padding': '5px 8px',
        '&.MuiChip-root': {
          height: 'auto',
        },
        '& span': {
          fontSize: isMobile ? '12px' : '14px',
          lineHeight: '120%',
          padding: 0,
        },
      }}
    />
  );
}

function TicketDescription({ description, category_name }) {
  const isDesc = !!description
    .replace(/<[^>]+>/g, '')
    .replace(/ /g, '')
    .replace(/&nbsp;/g, ' ');
  return (
    <Typography
      variant="caption3"
      component="div"
      sx={{
        color: (theme) => theme.palette.grey[500],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {isDesc
        ? description
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .substring(0, 80)
        : category_name}
    </Typography>
  );
}

TicketCard.Body = TicketCardBody;
TicketCard.Info = TicketCardInfo;
TicketCard.Action = TicketCardAction;
TicketCard.SoldOut = TicketCardSoldOut;
TicketCard.Price = TicketCardPrice;
TicketCard.Description = TicketDescription;

export default TicketCard;
